import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import RecipeRegisterFormStep1 from "../../forms/recipes/recipeRegisterFormStep1";
import RecipeRegisterFormStep2 from "../../forms/recipes/recipeRegisterFormStep2";
import RecipeNutritionalInfoForm from "../../forms/recipes/recipeNutritionalInfoForm";
import Stepper from "../../../../components/lib/stepperHeader";

export const STEP = {
    BASIC: 0,
    DETAILS: 1,
    NUTRITION: 2,
    SUMMARY: 3,
};

const steps = [
    { key: STEP.BASIC, label: "Básico" },
    { key: STEP.DETAILS, label: "Detalhes" },
    { key: STEP.NUTRITION, label: "Tabela Nutricional" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const Info = ({ label, value }) => (
    <div className="flex justify-between border-b pb-1">
        <span className="font-medium">{label}</span>
        <span>{value || "-"}</span>
    </div>
);

const defaultNutrition = [
    { nutrient: "Valor Energético", valueInPortion: "", dailyValue: "" },
    { nutrient: "Carboidratos", valueInPortion: "", dailyValue: "" },
    { nutrient: "Proteínas", valueInPortion: "", dailyValue: "" },
    { nutrient: "Gorduras Totais", valueInPortion: "", dailyValue: "" },
    { nutrient: "Fibra Alimentar", valueInPortion: "", dailyValue: "" },
    { nutrient: "Sódio", valueInPortion: "", dailyValue: "" },
];

const parseNutritionalFacts = (data) => {
    if (!data) return defaultNutrition;
    try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        return (Array.isArray(parsed) && parsed.length > 0) ? parsed : defaultNutrition;
    } catch (e) {
        return defaultNutrition;
    }
};

const parseIngredients = (data) => {
    if (!data || typeof data !== "string") return [];

    return data.split(',').map((raw, index) => {
        const parts = raw.trim().split(/\s*x\s*/);

        if (parts.length === 3) {
            return {
                product_id: parts[0].trim(),
                name: parts[1].trim(),
                amount: Number(parts[2]) || 1
            };
        }

        if (parts.length === 2) {
            return {
                product_id: `legacy-${index}`,
                name: parts[0].trim(),
                amount: Number(parts[1]) || 1
            };
        }

        return null;
    }).filter(Boolean);
};


const RecipeEditStepper = ({ recipe, initialImagePreview, onFinish, onCancel, onDirty }) => {
    const [step, setStep] = useState(STEP.BASIC);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            imageFile: null,
            imagePreview: initialImagePreview || null,
            name: recipe?.name || "",
            price: recipe?.price || "",
            online_price: recipe?.online_price ?? "",
            sku: recipe?.sku || "",
            active: recipe?.active ?? false,
            needsPreparation: recipe?.needsPreparation ?? false,
            category: recipe?.category || "",
            description: recipe?.description || "",
            observations: recipe?.observations || "",
            ingredients: parseIngredients(recipe?.ingredientsList || recipe?.item_list),
            nutrition: parseNutritionalFacts(recipe?.nutritionalFacts),
            clientCode: recipe?.clientCode
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Nome é obrigatório").max(50, "Máximo 50 caracteres"),
            price: Yup.number().typeError("Deve ser um número").required("Preço é obrigatório").positive("Deve ser maior que zero"),
            sku: Yup.string().max(30, 'Máximo 30 caracteres').nullable(),
            imageFile: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto do combo deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato inválido. Use JPEG, JPG ou PNG", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async (values) => {
            if (step < STEP.SUMMARY) {
                setStep(s => s + 1);
                return;
            }

            await onFinish({
                recipeValues: values,
                image: values.imageFile,
            });
        }
    });

    return (
        <form onSubmit={formik.handleSubmit} onChange={onDirty}>
            <Stepper
                steps={steps}
                currentStep={step}
            />

            {step === STEP.BASIC && <RecipeRegisterFormStep1 formik={formik} />}
            {step === STEP.DETAILS && <RecipeRegisterFormStep2 formik={formik} />}
            {step === STEP.NUTRITION && (
                <RecipeNutritionalInfoForm formik={formik} />
            )}
            {step === STEP.SUMMARY && (
                <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-1">
                        {formik.values.imagePreview ? (
                            <img
                                src={formik.values.imagePreview}
                                className="rounded-xl object-cover w-full h-56"
                                alt=""
                            />
                        ) : (
                            <div className="h-56 flex items-center justify-center bg-gray-100 rounded-xl">
                                Sem imagem
                            </div>
                        )}
                        <div className="py-2">
                            <Info label="Nome" value={formik.values.name} />
                            <Info label="Preço" value={`R$ ${formik.values.price}`} />
                             <Info label="Preço Online" value={formik.values.online_price ? `R$ ${formik.values.online_price}` : ""} />
                        </div>
                    </div>

                    <div className="col-span-2 space-y-4 text-sm">
                        <Info label="SKU" value={formik.values.sku} />
                        <Info label="Ativo" value={formik.values.active ? "Sim" : "Não"} />
                        <Info label="Precisa preparo" value={formik.values.needsPreparation ? "Sim" : "Não"} />
                        <Info label="Categoria" value={formik.values.category} />
                        <Info label="Descrição" value={formik.values.description} />
                        <Info label="Observações" value={formik.values.observations} />

                    </div>

                    <div className="col-span-3 border rounded-xl p-4">
                        <h4 className="font-semibold mb-2">Lista de Ingredientes</h4>
                        <div className="space-y-1">
                            {formik.values.ingredients.map((item, idx) => (
                                <div key={idx} className="text-sm flex justify-between">
                                    <span>{item.name}</span>
                                    <span className="font-mono">x {item.amount}</span>
                                </div>
                            ))}
                            {formik.values.ingredients.length === 0 && <p className="text-gray-400 text-sm">Nenhum ingrediente selecionado.</p>}
                        </div>
                    </div>

                    <div className="col-span-3 border rounded-xl p-4">
                        <h4 className="font-semibold mb-2">Tabela Nutricional</h4>
                        {formik.values.nutrition.map((row, index) => (
                            <div key={index} className="text-sm">
                                <strong>{row.nutrient}</strong>: {row.valueInPortion} ({row.dailyValue}%)
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-between mt-8">
                <button
                    type="button"
                    onClick={step === STEP.BASIC ? onCancel : () => setStep(step - 1)}
                    className="px-4 py-2 rounded-xl border"
                >
                    {step === STEP.BASIC ? "Cancelar" : "Voltar"}
                </button>

                <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="px-6 py-2 rounded-xl bg-blue-600 text-white"
                >
                    {step === STEP.SUMMARY ? "Concluir" : "Avançar"}
                </button>
            </div>
        </form>
    );
};

export default RecipeEditStepper;
